import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createTenant, AdminApiError } from '../../../shared/services/adminApi';

const industries = [
  { value: 'commercial-construction', label: 'Commercial Construction (GC)' },
  { value: 'painting', label: 'Painting' },
  { value: 'drywall', label: 'Drywall' },
  { value: 'roofing', label: 'Roofing' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'flooring', label: 'Flooring' },
  { value: 'concrete', label: 'Concrete' },
  { value: 'framing-carpentry', label: 'Framing / Carpentry' },
  { value: 'landscaping', label: 'Landscaping' },
  { value: 'other', label: 'Other' },
];

const tiers = [
  { value: 'free', label: 'Free', description: 'Basic features, 50 jobs max' },
  { value: 'pro', label: 'Pro', description: 'Full features, unlimited jobs' },
];

export function CreateTenant() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    companyName: '',
    subdomain: '',
    industry: 'commercial-construction',
    tier: 'pro',
    adminEmail: '',
    adminName: '',
    settings: {
      defaultMarkupPercent: 15,
      defaultProfitMargin: 10,
      defaultOverheadPercent: 12,
      timezone: 'America/New_York',
    },
    branding: {
      colors: {
        primary: '#2563eb',
        secondary: '#64748b',
        accent: '#10b981',
      },
    },
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSettingsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [field]: value,
      },
    }));
  };

  const handleSubdomainChange = (value) => {
    // Sanitize subdomain: lowercase, alphanumeric and hyphens only
    const sanitized = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    handleChange('subdomain', sanitized);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      // Validate
      if (!formData.companyName.trim()) {
        throw new Error('Company name is required');
      }
      if (!formData.subdomain.trim()) {
        throw new Error('Subdomain is required');
      }
      if (formData.subdomain.length < 3) {
        throw new Error('Subdomain must be at least 3 characters');
      }
      if (!formData.adminEmail.trim()) {
        throw new Error('Admin email is required');
      }

      await createTenant(formData);

      // Navigate to tenant list on success
      navigate('/admin');
    } catch (err) {
      console.error('Failed to create tenant:', err);
      if (err instanceof AdminApiError) {
        if (err.status === 401) {
          setError('Please log in to create a tenant');
        } else if (err.status === 403) {
          setError('You do not have permission to create tenants');
        } else if (err.status === 409) {
          setError('A tenant with this subdomain already exists');
        } else {
          setError(err.message || 'Failed to create tenant');
        }
      } else {
        setError(err.message || 'Failed to create tenant');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Link
            to="/admin"
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create New Tenant</h1>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 ml-8">
          Set up a new client account with their own branded portal
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company Information */}
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Company Information</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Company Name *
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => handleChange('companyName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Acme Construction"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Subdomain *
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  value={formData.subdomain}
                  onChange={(e) => handleSubdomainChange(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-l-lg focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="acme"
                />
                <span className="px-3 py-2 bg-gray-100 dark:bg-gray-800 border border-l-0 border-gray-300 dark:border-gray-700 rounded-r-lg text-gray-500 dark:text-gray-400 text-sm">
                  .quotethisjob.com
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Lowercase letters, numbers, and hyphens only
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Industry
              </label>
              <select
                value={formData.industry}
                onChange={(e) => handleChange('industry', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              >
                {industries.map(ind => (
                  <option key={ind.value} value={ind.value}>{ind.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Subscription Tier
              </label>
              <div className="grid grid-cols-2 gap-4">
                {tiers.map(tier => (
                  <label
                    key={tier.value}
                    className={`flex flex-col p-4 border rounded-lg cursor-pointer transition-colors ${
                      formData.tier === tier.value
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30'
                        : 'border-gray-300 dark:border-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <input
                      type="radio"
                      name="tier"
                      value={tier.value}
                      checked={formData.tier === tier.value}
                      onChange={(e) => handleChange('tier', e.target.value)}
                      className="sr-only"
                    />
                    <span className="font-medium text-gray-900 dark:text-gray-100">{tier.label}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{tier.description}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Admin User */}
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Initial Admin User</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            This user will receive an email with login credentials
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Admin Name *
              </label>
              <input
                type="text"
                value={formData.adminName}
                onChange={(e) => handleChange('adminName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="John Smith"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Admin Email *
              </label>
              <input
                type="email"
                value={formData.adminEmail}
                onChange={(e) => handleChange('adminEmail', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="john@acmeconstruction.com"
              />
            </div>
          </div>
        </div>

        {/* Default Settings */}
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Default Settings</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            These can be adjusted later by the client
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Default Markup %
              </label>
              <input
                type="number"
                value={formData.settings.defaultMarkupPercent}
                onChange={(e) => handleSettingsChange('defaultMarkupPercent', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Default Profit Margin %
              </label>
              <input
                type="number"
                value={formData.settings.defaultProfitMargin}
                onChange={(e) => handleSettingsChange('defaultProfitMargin', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Default Overhead %
              </label>
              <input
                type="number"
                value={formData.settings.defaultOverheadPercent}
                onChange={(e) => handleSettingsChange('defaultOverheadPercent', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                Timezone
              </label>
              <select
                value={formData.settings.timezone}
                onChange={(e) => handleSettingsChange('timezone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="America/New_York">Eastern (ET)</option>
                <option value="America/Chicago">Central (CT)</option>
                <option value="America/Denver">Mountain (MT)</option>
                <option value="America/Los_Angeles">Pacific (PT)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-end gap-4">
          <Link
            to="/admin"
            className="px-4 py-2 text-gray-700 dark:text-gray-200 text-sm font-medium hover:text-gray-900 dark:hover:text-white"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Creating...' : 'Create Tenant'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateTenant;
